import { useFormContext, useWatch } from "react-hook-form";

import { RobotoButton } from "@/shared/foundation/primitives";

import { submitKindFor } from "../formScope";
import { attentionCount, formStatusKind } from "../formStatus";
import { type TriggerFormValues } from "../triggerFormSchema";

import styles from "./FormActions.module.css";

type SubmitKind = ReturnType<typeof submitKindFor>;

/** The words for each submit the form can show. */
const SUBMIT_LABELS: Record<SubmitKind, string> = {
  create: "Create",
  "create-and-enable": "Create and enable",
  save: "Save changes",
};

/** What is left to do, said in terms of the submit the row is offering. */
const NOTHING_SUBMITTED_YET: Record<SubmitKind, string> = {
  create: "Fill in the sections above, then create the trigger.",
  "create-and-enable": "Fill in the sections above, then create the trigger.",
  save: "Change what you need above, then save the trigger.",
};

export interface FormActionsProps {
  mode: "create" | "edit";
  onCancel?: () => void;
}

function statusText(
  kind: ReturnType<typeof formStatusKind>,
  submitKind: SubmitKind,
  needingAttention: number,
): string {
  switch (kind) {
    case "unsubmitted":
      return NOTHING_SUBMITTED_YET[submitKind];
    case "clean":
      return "Everything checks out.";
    case "attention":
      return needingAttention === 1
        ? "1 field needs attention."
        : `${needingAttention} fields need attention.`;
  }
}

/**
 * The row the form's main column ends on: what still needs attention, a way
 * out, and the submit.
 *
 * The submit is never disabled for validity — a person has to be able to ask
 * what is wrong — only while a submission is in flight.
 */
export function FormActions({ mode, onCancel }: FormActionsProps) {
  const { control, formState } = useFormContext<TriggerFormValues>();
  const enabled = useWatch({ control, name: "enabled" });
  const needingAttention = attentionCount(formState.errors);
  const submitKind = submitKindFor(mode, enabled);
  const statusKind = formStatusKind(formState.isSubmitted, needingAttention);

  return (
    <div className={styles.actions} data-testid="trigger-form-actions">
      <p
        className={styles.status}
        data-testid="trigger-form-status"
        data-attention-count={needingAttention}
        data-status-kind={statusKind}
        data-status-submit-kind={submitKind}
        role="status"
      >
        {statusText(statusKind, submitKind, needingAttention)}
      </p>
      <div className={styles.buttons}>
        {onCancel !== undefined && (
          <RobotoButton variant="secondary" onClick={onCancel}>
            Cancel
          </RobotoButton>
        )}
        <RobotoButton
          type="submit"
          loading={formState.isSubmitting}
          data-testid="trigger-form-submit"
          data-submit-kind={submitKind}
        >
          {SUBMIT_LABELS[submitKind]}
        </RobotoButton>
      </div>
    </div>
  );
}
